"use client";

import { useLingui } from "@lingui/react/macro";
import { Button } from "@offstreetparking/design-system/forms";
import { CircleCheckIcon } from "@offstreetparking/design-system/icons/CircleCheckIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { Form } from "@offstreetparking/portal-flow/components/forms/Form";
import { ButtonRow } from "@offstreetparking/portal-flow/components/layouts/ButtonRow";
import { useMutation } from "@tanstack/react-query";
import { useId } from "react";
import * as z from "zod/v4";
import { OTPInput } from "~/components/auth/OTPInput";
import { useCompany } from "~/components/providers/CompanyProvider";
import { useResendCooldown } from "./useResendCooldown";
const verifyEmailSchema = z.object({
	code: z.string().min(6).max(6),
	pendingAuthenticationToken: z.string(),
	subdomain: z.string().optional(),
});
export type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>;
export type VerifyEmailFormContentProps = {
	token: string;
	isVerifying: boolean;
	verificationError: Error | null;
	onVerifyCode: (data: VerifyEmailFormData) => void;
	onResetVerification: () => void;
	onResendCode: () => Promise<void>;
};
export const VerifyEmailFormContent = ({
	token,
	onVerifyCode,
	isVerifying,
	verificationError,
	onResetVerification,
	onResendCode,
}: VerifyEmailFormContentProps) => {
	const { t } = useLingui();
	const formId = useId();
	const { company } = useCompany();
	const { cooldownSeconds, resetCooldown } = useResendCooldown();
	const resendMutation = useMutation({ mutationFn: onResendCode, onSuccess: resetCooldown });
	return (
		<div className="space-y-4">
			{verificationError && (
				<div className="rounded-md bg-critical-faded p-4">
					<Text size="sm" className="text-critical">
						{verificationError.message || t`Verification failed. Please check your code and try again.`}
					</Text>
				</div>
			)}

			<Form<VerifyEmailFormData>
				id={formId}
				className="space-y-6"
				schema={verifyEmailSchema}
				initialValues={{
					code: "",
					pendingAuthenticationToken: token,
					subdomain: company?.subdomain,
				}}
				onSubmit={(values) => {
					resendMutation.reset();
					onVerifyCode(values);
				}}
			>
				{(methods) => (
					<>
						<OTPInput
							name="code"
							label={t`Verification code`}
							autoFocus
							isRequired
							onValueChange={() => onResetVerification()}
							onComplete={() =>
								methods.handleSubmit((values) => {
									resendMutation.reset();
									onVerifyCode(values);
								})()
							}
						/>

						<ButtonRow className="flex-col gap-2">
							<Button isLoading={isVerifying} type="submit" form={formId} className="w-full">
								{t`Verify Email`}
							</Button>

							<Button
								type="button"
								intent="tertiary"
								className="w-full"
								isLoading={resendMutation.isPending}
								isDisabled={cooldownSeconds > 0 || resendMutation.isPending}
								onClick={() => {
									onResetVerification();
									resendMutation.mutate();
								}}
							>
								{cooldownSeconds > 0 ? t`Resend code (${cooldownSeconds}s)` : t`Resend code`}
							</Button>
						</ButtonRow>

						{resendMutation.isSuccess && !resendMutation.error && (
							<div className="my-4 flex items-center justify-center gap-2">
								<CircleCheckIcon className="size-5 shrink-0 text-success" aria-hidden="true" />
								<Text size="sm" className="text-success">
									{t`A new verification code has been sent.`}
								</Text>
							</div>
						)}

						{resendMutation.error && (
							<Text size="sm" className="text-critical">
								{resendMutation.error.message ||
									t`Could not resend the verification code. Please try again.`}
							</Text>
						)}
					</>
				)}
			</Form>
		</div>
	);
};
